type AlignmentCleanup = () => void;

const BREAKPOINT_QUERY = '(max-width: 959px)';

const CSS_VARS = [
  '--service-title-height',
  '--service-subtitle-height',
  '--service-booking-options-height',
  '--service-actions-height'
];

const clearVars = (listEl: HTMLElement): void => {
  CSS_VARS.forEach((variableName) => {
    listEl.style.removeProperty(variableName);
  });
};

const setVars = (
  listEl: HTMLElement,
  sizes: { title: number; subtitle: number; bookingOptions: number; actions: number }
): void => {
  listEl.style.setProperty('--service-title-height', `${sizes.title}px`);
  listEl.style.setProperty('--service-subtitle-height', `${sizes.subtitle}px`);
  listEl.style.setProperty('--service-booking-options-height', `${sizes.bookingOptions}px`);
  listEl.style.setProperty('--service-actions-height', `${sizes.actions}px`);
};

const getMaxHeights = (listEl: HTMLElement) => {
  const cards = Array.from(listEl.querySelectorAll('.service-item')) as HTMLElement[];
  let maxTitle = 0;
  let maxSubtitle = 0;
  let maxBookingOptions = 0;
  let maxActions = 0;

  cards.forEach((card) => {
    const title = card.querySelector('.service-title') as HTMLElement | null;
    const subtitle = card.querySelector('.service-subtitle') as HTMLElement | null;
    const bookingOptions = card.querySelector('.service-booking-options') as HTMLElement | null;
    const actions = card.querySelector('.service-actions') as HTMLElement | null;

    maxTitle = Math.max(maxTitle, title?.offsetHeight ?? 0);
    if (subtitle?.textContent?.trim()) {
      maxSubtitle = Math.max(maxSubtitle, subtitle.offsetHeight);
    }
    maxBookingOptions = Math.max(maxBookingOptions, bookingOptions?.offsetHeight ?? 0);
    maxActions = Math.max(maxActions, actions?.offsetHeight ?? 0);
  });

  return {
    title: maxTitle,
    subtitle: maxSubtitle,
    bookingOptions: maxBookingOptions,
    actions: maxActions
  };
};

export const initServiceListAlignment = (listEl: HTMLElement): AlignmentCleanup => {
  let resizeObserver: ResizeObserver | null = null;
  let rafId = 0;

  const update = () => {
    if (window.matchMedia(BREAKPOINT_QUERY).matches) {
      clearVars(listEl);
      return;
    }

    const sizes = getMaxHeights(listEl);
    setVars(listEl, sizes);
  };

  const scheduleUpdate = () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
    rafId = requestAnimationFrame(update);
  };

  const handleResize = () => scheduleUpdate();

  window.addEventListener('resize', handleResize);
  window.addEventListener('load', scheduleUpdate);

  scheduleUpdate();

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => scheduleUpdate());
    resizeObserver.observe(listEl);
  }

  return () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('load', scheduleUpdate);

    if (resizeObserver) {
      resizeObserver.disconnect();
    }

    clearVars(listEl);

    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
};
